"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { getTickets, getPlazasBySucursal, getMembresiasBySucursal, cancelTicket } from "../../services/controlService"
import { Car, Timer, AlertTriangle, CreditCard, Ticket, X, CheckCircle, Banknote, Plus, Search } from 'lucide-react'
import CobroModal from "../../components/CobroModal"
import IngresoModal from "../../components/IngresoModal"
import { normalizarPlaca } from "../../utils/ticketPrint"
import "./Control.css"

const Control = () => {
  const [tickets, setTickets] = useState([])
  const [membresias, setMembresias] = useState([])
  const [vehiculosIngresados, setVehiculosIngresados] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [plazas, setPlazas] = useState(0)
  
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [ticketToCancel, setTicketToCancel] = useState(null);
  const [cancelStatus, setCancelStatus] = useState('confirming');

  // Cobro
  const [cobroVehiculo, setCobroVehiculo] = useState(null);

  // Registro de ingreso (con impresión del ticket)
  const [isIngresoModalOpen, setIsIngresoModalOpen] = useState(false);

  // Búsqueda por placa
  const [busqueda, setBusqueda] = useState("");

  const handleCobroExitoso = (ticketId) => {
    setVehiculosIngresados(prev => prev.filter(v => v.id !== ticketId));
    setTickets(prev => prev.filter(t => t.id !== ticketId));
  };

  // ===== NUEVO: ESTADO PARA GUARDAR EL ROL DEL USUARIO =====
  const [userRole, setUserRole] = useState(null);

  const sucursalId = localStorage.getItem("idsucursal")
  
  // ===== NUEVO: LEER EL ROL DEL USUARIO DEL LOCALSTORAGE AL CARGAR =====
  useEffect(() => {
    const role = localStorage.getItem("role");
    setUserRole(role);
  }, []);


  // Se extrajo del useEffect para poder recargar la tabla después de
  // registrar un ingreso desde el modal.
  const cargarDatos = useCallback(async () => {
    try {
      if (sucursalId) {
        const [ticketsData, membresiasData] = await Promise.all([
          getTickets(sucursalId),
          getMembresiasBySucursal(sucursalId)
        ])
        
        setTickets(ticketsData)
        setMembresias(membresiasData)
        
        const vehiculosCombinados = [
          ...ticketsData.map(ticket => ({
            id: ticket.id,
            placa: ticket.placa,
            fechaIngreso: ticket.hentrada,
            tipo: 'ticket'
          })),
          ...membresiasData.map((membresia, index) => ({
            id: `M-${index + 1}`,
            placa: membresia.placa,
            fechaIngreso: membresia.fechaIngreso,
            tipo: 'membresia'
          }))
        ]
        
        vehiculosCombinados.sort((a, b) => new Date(b.fechaIngreso) - new Date(a.fechaIngreso))
        setVehiculosIngresados(vehiculosCombinados)
      } else {
        setError("No se encontró el ID de la sucursal.")
      }
    } catch (error) {
      setError("Error al cargar los datos.")
      console.error(error)
    } finally {
      setLoading(false)
    }
  }, [sucursalId])

  const cargarPlazas = useCallback(async () => {
    try {
      if (sucursalId) {
        const plazasData = await getPlazasBySucursal(sucursalId)
        setPlazas(plazasData)
      }
    } catch (error) {
      console.error("Error al cargar las plazas:", error)
    }
  }, [sucursalId])

  useEffect(() => {
    cargarDatos()
    cargarPlazas()
  }, [cargarDatos, cargarPlazas])

  // Filtro por placa. Se comparan placas normalizadas (sin guion ni espacios)
  // para que "abc-1234" también encuentre a "ABC1234".
  const vehiculosFiltrados = useMemo(() => {
    if (!busqueda) return vehiculosIngresados
    return vehiculosIngresados.filter((vehiculo) =>
      normalizarPlaca(vehiculo.placa).includes(busqueda)
    )
  }, [vehiculosIngresados, busqueda])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date())
    }, 1000)

    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        document.body.classList.add("mobile-view")
      } else {
        document.body.classList.remove("mobile-view")
      }
    }
    handleResize()
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }

  const calculateElapsedTime = (entryTime) => {
    const entryDate = new Date(entryTime)
    const diffMs = currentTime - entryDate
    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000)
    const formattedHours = hours.toString().padStart(2, "0")
    const formattedMinutes = minutes.toString().padStart(2, "0")
    const formattedSeconds = seconds.toString().padStart(2, "0")
    return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`
  }

  /**
   * Atajo para la salida: si la búsqueda dejó un único ticket, Enter abre
   * directo el cobro. Las membresías no se cobran, así que se ignoran.
   */
  const cobrarDesdeBusqueda = (e) => {
    if (e.key !== "Enter") return
    const cobrables = vehiculosFiltrados.filter((v) => v.tipo === "ticket")
    if (cobrables.length === 1) setCobroVehiculo(cobrables[0])
  }

  const getTimeClass = (entryTime) => {
    const entryDate = new Date(entryTime)
    const diffMs = currentTime - entryDate
    const hours = diffMs / (1000 * 60 * 60)
    if (hours < 1) return "time-short"
    if (hours < 3) return "time-medium"
    return "time-long"
  }
  
  const openCancelConfirmation = (ticket) => {
    setTicketToCancel(ticket);
    setCancelStatus('confirming');
    setIsCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setIsCancelModalOpen(false);
    setTimeout(() => {
      setTicketToCancel(null);
    }, 300);
  };

  const confirmCancellation = async () => {
    if (!ticketToCancel) return;

    try {
      await cancelTicket(ticketToCancel.id);
      
      setVehiculosIngresados(prev => prev.filter(v => v.id !== ticketToCancel.id));
      setTickets(prev => prev.filter(t => t.id !== ticketToCancel.id));
      
      setCancelStatus('success');

      setTimeout(() => {
        closeCancelModal();
      }, 1500);

    } catch (error) {
      console.error("Error al cancelar el ticket:", error);
      alert(`No se pudo cancelar el ticket: ${error.message}`);
      closeCancelModal();
    }
  };

  return (
    <div className="control-container">
      <div className="control-header">
        <h1 className="title-control">Registro de Vehículos</h1>
        <div className="header-decoration"></div>
      </div>

      {error ? (
        <div className="error-alert">
          <div className="error-icon"><AlertTriangle size={24} /></div>
          <div className="error-content">
            <h4 className="error-title">Error</h4>
            <p className="error-message">{error}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="dashboard-stats">
            {/* ... tarjetas de estadísticas sin cambios ... */}
            <div className="stat-card">
              <div className="stat-icon"><Car size={24} className="lucide-icon" /></div>
              <div className="stat-content">
                <div className="stat-value">{vehiculosIngresados.length} / {plazas || "?"}</div>
                <div className="stat-label">Vehículos / Plazas totales{plazas && <div className="stat-sublabel">{plazas - vehiculosIngresados.length} plazas disponibles</div>}</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><Ticket size={24} className="lucide-icon" /></div>
              <div className="stat-content">
                <div className="stat-value">{tickets.length}</div>
                <div className="stat-label">Tickets activos<div className="stat-sublabel">Vehículos con ticket</div></div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><CreditCard size={24} className="lucide-icon" /></div>
              <div className="stat-content">
                <div className="stat-value">{membresias.length}</div>
                <div className="stat-label">Membresías activas<div className="stat-sublabel">Vehículos con membresía</div></div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon"><Timer size={24} className="lucide-icon" /></div>
              <div className="stat-content">
                <div className="stat-value">{currentTime.toLocaleTimeString("es-ES")}</div>
                <div className="stat-label">Hora actual</div>
              </div>
            </div>
          </div>

          <div className="table-container">
            <div className="table-header">
              <h2 className="table-title">
                Vehículos Ingresados
                {busqueda && (
                  <span className="table-contador">{vehiculosFiltrados.length} de {vehiculosIngresados.length}</span>
                )}
              </h2>
              <div className="table-header-actions">
                {/* Buscador de placa: para ubicar rápido el vehículo al cobrar la salida */}
                <div className="control-buscador">
                  <Search size={16} className="control-buscador-icono" />
                  <input
                    type="text"
                    className="control-buscador-input"
                    placeholder="Buscar placa…"
                    value={busqueda}
                    onChange={(e) => setBusqueda(normalizarPlaca(e.target.value))}
                    onKeyDown={cobrarDesdeBusqueda}
                    aria-label="Buscar vehículo por placa"
                    title="Enter abre el cobro cuando la búsqueda deja un solo ticket"
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {busqueda && (
                    <button className="control-buscador-limpiar" onClick={() => setBusqueda("")} title="Limpiar búsqueda" aria-label="Limpiar búsqueda">
                      <X size={14} />
                    </button>
                  )}
                </div>
                {/* Registro de ingreso + impresión del ticket, igual que la app móvil */}
                <button className="ingreso-button" onClick={() => setIsIngresoModalOpen(true)} title="Registrar ingreso e imprimir ticket">
                  <Plus size={16} /> Registrar ingreso
                </button>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table-tickets">
                <thead>
                  <tr>
                    <th className="column-id">ID</th>
                    <th className="column-tipo">Tipo</th>
                    <th className="column-placa">Placa</th>
                    <th className="column-entrada">Hora de Entrada</th>
                    <th className="column-tiempo"><div className="time-header"><span>Tiempo en parqueadero</span></div></th>
                    {/* Columna de acciones: visible para TODOS los roles (Cobrar) */}
                    <th className="column-cancelacion">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={`skeleton-${index}`} className="skeleton-row">
                        <td className="column-id"><div className="skeleton-cell"></div></td>
                        <td className="column-tipo"><div className="skeleton-cell"></div></td>
                        <td className="column-placa"><div className="skeleton-cell"></div></td>
                        <td className="column-entrada"><div className="skeleton-cell"></div></td>
                        <td className="column-tiempo"><div className="skeleton-cell"></div></td>
                        <td className="column-cancelacion"><div className="skeleton-cell"></div></td>
                      </tr>
                    ))
                  ) : vehiculosFiltrados.length > 0 ? (
                    vehiculosFiltrados.map((vehiculo, index) => (
                      <tr key={`${vehiculo.tipo}-${vehiculo.id}-${index}`}>
                        <td className="column-id"><span className="ticket-id">{vehiculo.id}</span></td>
                        <td className="column-tipo"><span className={`tipo-badge ${vehiculo.tipo === 'ticket' ? 'tipo-ticket' : 'tipo-membresia'}`}>{vehiculo.tipo === 'ticket' ? (<><Ticket size={14} className="tipo-icon" />Ticket</>) : (<><CreditCard size={14} className="tipo-icon" />Membresía</>)}</span></td>
                        <td className="column-placa"><span className="placa-badge">{vehiculo.placa}</span></td>
                        <td className="column-entrada fecha-hora">{formatDateTime(vehiculo.fechaIngreso)}</td>
                        <td className="column-tiempo"><div className={`elapsed-time ${getTimeClass(vehiculo.fechaIngreso)}`}>{calculateElapsedTime(vehiculo.fechaIngreso)}</div></td>
                        <td className="column-cancelacion">
                          {vehiculo.tipo === 'ticket' && (
                            <div className="control-action-buttons">
                              {/* Cobrar: disponible para TODOS los roles */}
                              <button className="cobrar-button" onClick={() => setCobroVehiculo(vehiculo)} title="Cobrar Ticket">
                                <Banknote size={15} /> Cobrar
                              </button>
                              {/* Cancelar: solo ADMIN/DEVELOPER */}
                              {(userRole === 'ADMIN' || userRole === 'DEVELOPER') && (
                                <button className="cancel-button" onClick={() => openCancelConfirmation(vehiculo)} title="Cancelar Ticket">Cancelar</button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="empty-message">
                        <div className="empty-state">
                          <div className="empty-icon">{busqueda ? "🔍" : "🚫"}</div>
                          {busqueda ? (
                            <>
                              <p>Ningún vehículo con la placa <strong>{busqueda}</strong>.</p>
                              <button className="empty-limpiar" onClick={() => setBusqueda("")}>Ver todos los vehículos</button>
                            </>
                          ) : (
                            <p>No hay vehículos registrados.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {isCancelModalOpen && (
        <div className="modal-overlay">
          <div className="modal-container">
            <div className="modal-header">
              <h3 className="modal-title">{cancelStatus === 'confirming' ? 'Confirmar Cancelación' : 'Éxito'}</h3>
              <button onClick={closeCancelModal} className="modal-close-button"><X size={20} /></button>
            </div>
            
            {cancelStatus === 'confirming' && (
              <>
                <div className="modal-content">
                  <div className="modal-warning-icon"><AlertTriangle size={48} /></div>
                  <div className="modal-warning-text">
                    <p className="main-warning">¿Está seguro de que desea cancelar este ticket?</p>
                    <p className="secondary-warning">Esta acción no se puede deshacer y afectará al ticket <strong>#{ticketToCancel?.id}</strong> con placa <strong>{ticketToCancel?.placa}</strong>.</p>
                  </div>
                </div>
                <div className="modal-actions">
                  <button onClick={closeCancelModal} className="modal-btn modal-btn-secondary">Volver</button>
                  <button onClick={confirmCancellation} className="modal-btn modal-btn-danger">Sí, cancelar</button>
                </div>
              </>
            )}

            {cancelStatus === 'success' && (
              <div className="modal-content-success">
                <CheckCircle className="modal-success-icon" size={64} />
                <p className="modal-success-text">¡Ticket Cancelado!</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de registro de ingreso (crea el ticket e imprime) */}
      {isIngresoModalOpen && (
        <IngresoModal
          sucursalId={sucursalId}
          onClose={() => setIsIngresoModalOpen(false)}
          onSuccess={cargarDatos}
        />
      )}

      {/* Modal de cobro (disponible para todos los roles) */}
      {cobroVehiculo && (
        <CobroModal
          vehiculo={cobroVehiculo}
          sucursalId={sucursalId}
          role={userRole}
          onClose={() => setCobroVehiculo(null)}
          onSuccess={handleCobroExitoso}
        />
      )}
    </div>
  )
}

export default Control